import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { apiConstants, postGeneric } from '../../../common/constants';
import {
    BUCKET_KEYS,
    PILL_KEYS,
    buildBaselineAndFilter,
    buildBaselineOrFilter,
    buildPillFilter,
    fmt,
} from './constants';

/**
 * Single-call replacement for useAppointmentKpi + per-page analytics fetches.
 *
 * Backed by POST /api/order/appointment_delivery/dashboard which runs one
 * $facet aggregation across the same baseline /order/search uses, so grid +
 * dashboard share scope by construction.
 *
 * Pass `include` to request only the blocks you need:
 *   - 'buckets'   → pill counts (mirrors multi_count)
 *   - 'summary'   → headline metrics (totalAppts, abdActiveOpen, ...)
 *   - 'perf'      → group-by metrics (use `groupBy: ['courier','merchant']`)
 *   - 'reasons'   → closure reason breakdown
 *   - 'parties'   → ABD party master + stats (paginated)
 */
const EMPTY_STATE = {
    buckets: {},
    summary: {},
    perf: {},
    reasons: [],
    parties: { data: [], total: 0, page: 0, size: 0 },
    // Convenience pill aliases for FE migration parity with useAppointmentKpi.
    total: 0,
    active: 0,
    miss1: 0,
    miss2: 0,
    miss3plus: 0,
    needsScheduling: 0,
    delivered: 0,
    prozoFault: 0,
    nonProzo: 0,
    eventuallyDelivered: 0,
    prozoFaultPending: 0,
    missedAny: 0,
    abdActiveOpen: 0,
    avgDaysPending: 0,
    resolvedToday: 0,
    totalAppts: 0,
    firstAttemptRate: 0,
    avgAttempts: 0,
    prozoMissRate: 0,
    abdLast3m: 0,
};

const stableSortedKey = (arr) => (arr || []).slice().sort().join(',');

export default function useAppointmentDashboard({
    filters = {},
    include = ['buckets', 'summary'],
    groupBy = [],
    extraBaseline = [],
    partiesPage = 0,
    partiesSize = 50,
    partiesSearch = '',
    partiesAbdFilter = '',
    reload = 0,
}) {
    const [data, setData] = useState(EMPTY_STATE);
    // Start in loading so pages don't flash EMPTY_STATE zeros before the first fetch resolves.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Counter so a slower fetch firing after a faster one (filter change burst)
    // can't clobber the latest result. Increments on each new request; the
    // resolver checks it's still the latest before writing state.
    const reqIdRef = useRef(0);

    const from = fmt(filters.fromDate);
    const to = fmt(filters.toDate);
    const merchantOid = filters.merchant || '';
    const courierProzoId = filters.courier || '';
    const city = filters.city || '';
    const state = filters.state || '';
    const awb = filters.awb || '';
    const missCategory = filters.missCategory || '';
    const attempts = filters.attempts || '';
    const documents = filters.documents || '';
    const reference = filters.reference || '';
    // Memoize keys so refs are stable when caller passes inline arrays.
    const extraBaselineKey = useMemo(() => JSON.stringify(extraBaseline || []), [extraBaseline]);
    const inclKey = useMemo(() => stableSortedKey(include), [include]);
    const grpKey = useMemo(() => stableSortedKey(groupBy), [groupBy]);

    const fetchDashboard = useCallback(async () => {
        const myId = ++reqIdRef.current;
        setLoading(true);
        setError(null);
        try {
            // Use the SAME baseline builders as useAppointmentSearch so dashboard
            // bucket counts match grid totals when the same pill is applied.
            const filterArgs = {
                fromDate: filters.fromDate,
                toDate: filters.toDate,
                merchant: merchantOid,
                courier: courierProzoId,
                city,
                state,
                awb,
                missCategory,
                attempts,
                documents,
                reference: filters.reference || '',
            };
            const baselineAnd = [...buildBaselineAndFilter(filterArgs), ...extraBaseline];
            const baselineOr = buildBaselineOrFilter(filterArgs);

            const wantBuckets = include.includes('buckets');
            const buckets = wantBuckets
                ? BUCKET_KEYS.map((k) => ({ name: k, andfilter: buildPillFilter(k) }))
                : undefined;

            // Explicit summary scope so backend doesn't reverse-extract from $match.
            // Send for any block that uses no-date/current-state metrics.
            const summaryScope = (include.includes('summary') || include.includes('parties'))
                ? {
                    merchantOid: merchantOid || undefined,
                    courierProzoId: courierProzoId || undefined,
                    city: city || undefined,
                    state: state || undefined,
                }
                : undefined;

            const body = {
                baseline: { andfilter: baselineAnd, orfilter: baselineOr },
                summaryScope,
                include,
                buckets,
                groupBy: groupBy.length ? groupBy : undefined,
                partiesPage: include.includes('parties') ? partiesPage : undefined,
                partiesSize: include.includes('parties') ? partiesSize : undefined,
                partiesSearch: include.includes('parties') && partiesSearch ? partiesSearch : undefined,
                partiesAbdFilter: include.includes('parties') && partiesAbdFilter ? partiesAbdFilter : undefined,
            };

            const res = await postGeneric(apiConstants.APPOINTMENT_DELIVERY_DASHBOARD, body);
            if (myId !== reqIdRef.current) return;
            const obj = res?.data?.responseObj || {};

            const bucketsMap = obj.buckets || {};
            const summary = obj.summary || {};
            const merged = {
                ...EMPTY_STATE,
                ...summary,
                buckets: bucketsMap,
                summary,
                perf: obj.perf || {},
                reasons: obj.reasons || [],
                parties: obj.parties || EMPTY_STATE.parties,
                // Pill aliases for parity with old useAppointmentKpi shape.
                total: bucketsMap[PILL_KEYS.ALL] ?? 0,
                active: bucketsMap[PILL_KEYS.ACTIVE] ?? 0,
                miss1: bucketsMap[PILL_KEYS.MISS_1] ?? 0,
                miss2: bucketsMap[PILL_KEYS.MISS_2] ?? 0,
                miss3plus: bucketsMap[PILL_KEYS.MISS_3_PLUS] ?? 0,
                needsScheduling: bucketsMap[PILL_KEYS.NEEDS_SCHEDULING] ?? 0,
                delivered: bucketsMap[PILL_KEYS.DELIVERED] ?? 0,
                prozoFault: bucketsMap[PILL_KEYS.PROZO_FAULT] ?? 0,
                nonProzo: bucketsMap[PILL_KEYS.NON_PROZO] ?? 0,
                eventuallyDelivered: bucketsMap[PILL_KEYS.EVENTUALLY_DELIVERED] ?? 0,
                prozoFaultPending: bucketsMap[PILL_KEYS.PROZO_FAULT_PENDING] ?? 0,
                missedAny: bucketsMap[PILL_KEYS.MISSED_ANY] ?? 0,
            };
            setData(merged);
        } catch (e) {
            if (myId !== reqIdRef.current) return;
            setError(e);
            setData(EMPTY_STATE);
        }
        if (myId === reqIdRef.current) setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, merchantOid, courierProzoId, city, state, awb, missCategory, attempts, documents, reference,
        extraBaselineKey, inclKey, grpKey, partiesPage, partiesSize, partiesSearch, partiesAbdFilter, reload]);

    // Single source of truth: filter / pagination / `reload` all flow through
    // fetchDashboard's deps. Stale-response guard above keeps state consistent
    // when the user changes filters faster than the network resolves.
    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    return { data, loading, error, refetch: fetchDashboard };
}
